import { sprintf } from 'sprintf-js';
import * as actionTypes from '../actions/action-types';
import {
	AppState, TargetType, ProcessState, Position,
} from '../appMode';
import { updateObject } from '../utility';
import { RESPONSIVE_MODE } from '../../../../modules/core/constants';

const i18n = window.dpIntroTourPublicConfig.i18n;

const initialState = {
	appState: AppState.INIT,
	savingState: ProcessState.IDLE,
	apiReqState: ProcessState.IDLE,
	prevAppState: AppState.INIT,
	targetType: TargetType.STEP,
	hintConfig: {},
	origin: '',
	isChange: false,
	stepChanges: {},
	triggerChanges: {},
	mobileMenuChanges: {},
	urlVarExamplesChanges: {},
	backupChanges: {},
	stepItem: null,
	stepCount: 0,
	stepChangingInProgress: false,
	stepAddedRemovedHist: [],
	totalSavedChanges: 0,
	isPRO: false,
	controlsPosition: Position.BOTTOM,
	mobileMenuOpenerSelected: false,
	mobileAltStepMode: null,
	isNavigButtonNextHovered: false,
	isNavigButtonBackHovered: false,
	isTargetDetailOpened: false,
	isCloseMenuOpened: false,
	isInteractionDetailOpened: false,
	cancelButtonAltPos: false
};

const isObjectEmpty = (obj) => Object.keys(obj).length === 0 && obj.constructor === Object;

const isAnyChange = (state) => !(isObjectEmpty(state.stepChanges)
	&& isObjectEmpty(state.triggerChanges)
	&& isObjectEmpty(state.mobileMenuChanges)
	&& isObjectEmpty(state.urlVarExamplesChanges)
	&& !state.stepAddedRemovedHist.length
);

const addChange = (initState, targetType, id, change) => {
	const targetStateName = `${targetType}Changes`;
	const newState = { ...initState };
	if (!initState[targetStateName]) newState[targetStateName] = {};
	if (!newState[targetStateName][id]) newState[targetStateName][id] = change;
	else if (change && typeof (change) === 'object') newState[targetStateName][id] = updateObject(initState[targetStateName][id], change);
	else newState[targetStateName][id] = change;
	newState.isChange = isAnyChange(newState);
	return newState;
};

const clearChanges = (initState, targetType) => {
	const targetStateName = `${targetType}Changes`;
	const newState = { ...initState };
	newState[targetStateName] = {};
	newState.isChange = isAnyChange(newState);
	return newState;
};

const backupAllChanges = (initState) => {
	const newState = { ...initState };
	newState.backupChanges = {
		stepChanges: { ...initState.stepChanges },
		triggerChanges: { ...initState.triggerChanges },
		mobileMenuChanges: { ...initState.mobileMenuChanges },
		urlVarExamplesChanges: { ...initState.urlVarExamplesChanges },
		stepAddedRemovedHist: [...initState.stepAddedRemovedHist],
	};
	return newState;
};

const restoreAllChanges = (initState) => {
	const newState = { ...initState };
	newState.stepChanges = { ...initState.backupChanges.stepChanges };
	newState.triggerChanges = { ...initState.backupChanges.triggerChanges };
	newState.mobileMenuChanges = { ...initState.backupChanges.mobileMenuChanges };
	newState.urlVarExamplesChanges = { ...initState.backupChanges.urlVarExamplesChanges };
	newState.stepAddedRemovedHist = [...initState.backupChanges.stepAddedRemovedHist];
	newState.isChange = isAnyChange(newState);
	return newState;
};

const clearAllChanges = (initState) => {
	const newState = backupAllChanges(initState);
	newState.stepChanges = {};
	newState.triggerChanges = {};
	newState.mobileMenuChanges = {};
	newState.urlVarExamplesChanges = {};
	newState.isChange = false;
	newState.stepAddedRemovedHist = [];
	return newState;
};

const updateNavigButtonHoveredState = (initState, id, hoverIn) => {
	const newState = { ...initState };
	switch (id) {
		case 'next': newState.isNavigButtonNextHovered = hoverIn == true; break;
		case 'back': newState.isNavigButtonBackHovered = hoverIn == true; break;
	}
	return newState;
};

const setAppState = (initState, newAppState) => {
	const newState = { ...initState };
	newState.appState = newAppState;
	newState.prevAppState = initState.appState;

	let title = '';
	let cta = '';
	let afterCtaText = '';
	if (initState.targetType === TargetType.MOBILE_MENU) {
		title = !initState.mobileMenuOpenerSelected ? i18n.open_mob_menu_prompt : i18n.close_mob_menu_prompt;
	} else {
		let target = initState.targetType;
		if (initState.targetType === TargetType.STEP && initState.mobileAltStepMode) {
			switch (initState.mobileAltStepMode) {
				case RESPONSIVE_MODE.mobileMenu:
					target = i18n.mobile_menu_alt;
					cta = i18n.did_not_menu_open_q;
					afterCtaText = i18n.did_not_menu_open_q_2;
					break;
				case RESPONSIVE_MODE.mobile: target = i18n.mobile_alt;
					break;
			}
		}
		title = sprintf(i18n.select_target_prompt, target);
	}

	const targetSelHint = {
		active: true,
		type: 'info',
		pulseInfinite: true,
		dismissible: true,
		title,
		cta,
		afterCtaText,
	};

	switch (newAppState) {
		case AppState.TARGET_SELECTION: newState.hintConfig = targetSelHint; break;
		default: if (initState.savingState !== ProcessState.ERROR && newAppState !== AppState.VIEW || initState.appState === AppState.TARGET_SELECTION) {
			newState.hintConfig = targetSelHint;
			newState.hintConfig.active = false;
		}
	}
	return newState;
};

const setProcessState = (initState, targetStateName, processState, error, errorDetail) => {
	const newState = { ...initState };
	newState[targetStateName] = processState;
	const targetSelHint = {
		active: true,
		type: 'error',
		pulse: true,
		dismissible: true,
		title: error,
		desc: errorDetail,
	};
	switch (processState) {
		case ProcessState.ERROR: newState.hintConfig = targetSelHint; break;
		case ProcessState.PROCESSING: {
			newState.hintConfig = targetSelHint;
			newState.hintConfig.active = false;
		}
			break;
		case ProcessState.PROCESSED:
			{
				newState.hintConfig = targetSelHint;
				newState.hintConfig.active = false;
			}
			break;
	}
	return newState;
};

const closeHint = (initState) => {
	const newState = { ...initState };
	newState.hintConfig = { ...initState.hintConfig, active: false };
	return newState;
};

const addNewStepAt = (initState, stepIdx, newStepCount) => {
	const newState = { ...initState };
	const stepChangesNew = [];
	for (let i = 0; i < newStepCount; i++) {
		if (i < stepIdx) {
			if (initState.stepChanges[i]) stepChangesNew[i] = initState.stepChanges[i];
		} else if (i > stepIdx) {
			if (initState.stepChanges[i - 1]) {
				stepChangesNew[i] = { ...initState.stepChanges[i - 1] };
			}
		}
	}
	newState.stepAddedRemovedHist.push({ idx: stepIdx, action: 'add' });
	newState.stepChanges = stepChangesNew;
	newState.isChange = isAnyChange(newState);
	return newState;
};

const removedStepAt = (initState, stepIdx, oldStepCount) => {
	const newState = { ...initState };
	const stepChangesNew = [];
	for (let i = 0; i < oldStepCount; i++) {
		if (i < stepIdx) {
			if (initState.stepChanges[i]) stepChangesNew[i] = initState.stepChanges[i];
		} else if (i >= stepIdx) {
			if (initState.stepChanges[i + 1]) {
				stepChangesNew[i] = { ...initState.stepChanges[i + 1] };
			}
		}
	}
	newState.stepAddedRemovedHist.push({ idx: stepIdx, action: 'remove' });
	newState.stepChanges = stepChangesNew;
	newState.isChange = isAnyChange(newState);
	return newState;
};


const reducer = (state = initialState, action) => {
	switch (action.type) {
		case actionTypes.SET_APP_STATE: return setAppState(state, action.appState);
		case actionTypes.SET_PROCESS_STATE: return setProcessState(state, action.stateName, action.processState, action.error, action.errorDetail);
		case actionTypes.SET_APP_EDIT_TARGET_TYPE: return updateObject(state, { targetType: action.targetType });
		case actionTypes.UPDATE_HINT: return updateObject(state, { hintConfig: action.hintConfig });
		case actionTypes.CLOSE_HINT: return closeHint(state);
		case actionTypes.ADD_CHANGE: return addChange(state, action.targetType, action.id, action.change);
		case actionTypes.CLEAR_CHANGES: return clearChanges(state, action.targetType);
		case actionTypes.CLEAR_ALL_CHANGES: return clearAllChanges(state);
		case actionTypes.RESTORE_ALL_CHANGES: return restoreAllChanges(state);
		case actionTypes.ON_ADD_NEW_STEP_AT: return addNewStepAt(state, action.stepIdx, action.newStepCount);
		case actionTypes.ON_REMOVE_STEP_AT: return removedStepAt(state, action.stepIdx, action.oldStepCount);
		case actionTypes.SET_CURRENT_STEP_CORE: return updateObject(state, { stepItem: action.stepItem });
		case actionTypes.SET_STEP_COUNT: return updateObject(state, { stepCount: action.stepCount });
		case actionTypes.STEP_CHANGING_IN_PROGRESS: return updateObject(state, { stepChangingInProgress: action.stepChangingInProgress });
		case actionTypes.SET_TARGET_TYPE: return updateObject(state, { targetType: action.targetType });
		case actionTypes.SET_ORIGIN: return updateObject(state, { origin: action.origin });
		case actionTypes.SET_TOTAL_SAVED_CHANGES: return updateObject(state, { totalSavedChanges: action.increment ? state.totalSavedChanges + action.totalSavedChanges : action.totalSavedChanges });
		case actionTypes.SET_CONTROLS_POSITION: return updateObject(state, { controlsPosition: action.controlsPosition });
		case actionTypes.SET_MOBILE_MENU_OPENER_SELECTED: return updateObject(state, { mobileMenuOpenerSelected: action.mobileMenuOpenerSelected });
		case actionTypes.SET_MOBILE_ALT_STEP_MODE: return updateObject(state, { mobileAltStepMode: action.mobileAltStepMode });
		case actionTypes.UPDATE_NAVIG_BUTTON_HOVERED: return updateNavigButtonHoveredState(state, action.id, action.hoverIn);
		case actionTypes.UPDATE_TARGET_EDIT_DETAIL_OPENED: return updateObject(state, { isTargetDetailOpened: action.isOpened });
		case actionTypes.UPDATE_CLOSE_MENU_OPENED: return updateObject(state, { isCloseMenuOpened: action.isOpened });
		case actionTypes.UPDATE_INTERACTION_EDIT_DETAIL_OPENED: return updateObject(state, { isInteractionDetailOpened: action.isOpened });
		case actionTypes.TOGGLE_CANCEL_BUTTON_POS: return updateObject(state, { cancelButtonAltPos: !state.cancelButtonAltPos });
		case actionTypes.SET_CANCEL_BUTTON_POS: return updateObject(state, { cancelButtonAltPos: action.position });
		default: return state;
	}
};

export default reducer;
